/*
 * Pack upgrades: swap a cart line for a larger pack.
 *
 * Deliberately a file rather than a script tag inside the snippet. The cart drawer
 * re-renders itself by fetching `?section_id=cart-drawer` and writing the result into
 * `.drawer__inner` with innerHTML, and scripts inserted that way never execute. An
 * inline handler therefore worked on a fresh page load and silently stopped working the
 * moment anything changed the cart, which is exactly when this module matters.
 *
 * Listening on `document` for the same reason: the markup it reacts to is replaced
 * repeatedly, so nothing can be bound to it directly.
 *
 * This file no longer tries to keep the drawer's copy fresh. Two attempts were made and
 * both were worse than the problem. Dispatching the theme's `cart-drawer:refresh` on
 * open repaints mid-animation and visibly doubles the drawer. Injecting only this block
 * puts a live offer inside a drawer that still says the cart is empty, because the
 * subscription widget updates the cart without telling the theme to re-render.
 *
 * That staleness is a fault between the theme and the subscription widget and it shows
 * with or without this module: add a subscription from a product page, open the drawer,
 * and it reports an empty cart. It needs fixing on its own terms, and once it is fixed
 * the drawer will carry these offers with no change here, because they are rendered
 * server side with the rest of the drawer.
 */
document.addEventListener("click", function (event) {
  var button = event.target.closest(".wn-upsell__button");
  if (!button || button.disabled) return;

  var lineKey = button.dataset.lineKey;
  var variantId = Number(button.dataset.variantId);
  var quantity = Number(button.dataset.lineQuantity) || 1;
  var sellingPlan = button.dataset.sellingPlan;
  if (!lineKey || !variantId) return;

  // Hidden line property (leading underscore: the cart and checkout skip it, the order in
  // admin shows it), so an order says whether this pack came from the cart's ladder rather
  // than the product page. The only way to count what these buttons sell.
  var source = button.classList.contains("wn-upsell__button--down") ? "cart-downgrade" : "cart-upgrade";
  var newItem = { id: variantId, quantity: quantity, properties: { _added_from: source } };
  // Present only when the line being replaced is a subscription and the larger pack
  // offers the same schedule. Liquid drops the offer entirely when it does not.
  if (sellingPlan) newItem.selling_plan = Number(sellingPlan);

  button.disabled = true;
  button.dataset.busy = "true";

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

  // Add first, then remove. Removing first empties a one line cart, and the app's drawer
  // watches the cart and repaints, so the customer saw their cart flash empty before the
  // new pack appeared. Adding first means the cart is never short of what they had.
  //
  // The order is safe here because the pack being added is a different product from the
  // one being removed, so there is no line for the cart to merge it into. If that pack
  // already sits in the cart on its own line, the source property keeps the two apart:
  // the cart shows two lines of the same pack, with the right total.
  fetch(root + "cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items: [newItem] }),
  })
    .then(function (response) {
      if (!response.ok) throw new Error("add failed");
      return fetch(root + "cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: lineKey, quantity: 0 }),
      });
    })
    .then(function (response) {
      if (!response.ok) throw new Error("change failed");
      settle();
    })
    .catch(function () {
      // Fall back to a reload: the cart is the source of truth, and the customer needs
      // to see what actually happened rather than a stale page.
      window.location.reload();
    });
});

/*
 * Show the new cart without throwing the customer back to the top of the page.
 *
 * Inside the upsell app's drawer a reload closes the drawer and loses the customer's
 * place, for a change they made without leaving the cart. That app refreshes its own
 * drawer on an `ocu:cart:update` event on `window`, and uses that same event itself to
 * pick up carts changed by other apps, so this is its own supported path rather than a
 * poke at its internals. The injected offers follow, because they are rebuilt whenever
 * the drawer's rows change.
 *
 * Everywhere else, on the cart page and in the theme's own drawer, a reload is still
 * the honest answer: those are rendered by Liquid and there is nothing to update in
 * place.
 */
/*
 * The count on the bag icon in the header.
 *
 * The theme redraws it from the `cart-icon-bubble` section whenever its own cart
 * code changes the cart. Nothing here goes through that code, and neither does
 * the app's drawer, so after an add or a remove the header went on showing the
 * old number until the page was reloaded. Same section, same swap the theme
 * does: fetch it, take the section's inner markup, put it in the anchor.
 *
 * On `window` so cart-upgrades-inject.js can call it after the app's drawer
 * changes too, which is how a remove done with the app's own control reaches
 * the header.
 */
window.wnRefreshCartBubble = function () {
  var bubble = document.getElementById("cart-icon-bubble");
  if (!bubble) return;
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  fetch(root + "?section_id=cart-icon-bubble")
    .then(function (response) {
      if (!response.ok) throw new Error("bubble failed");
      return response.text();
    })
    .then(function (html) {
      var section = new DOMParser().parseFromString(html, "text/html").querySelector(".shopify-section");
      if (section) bubble.innerHTML = section.innerHTML;
    })
    .catch(function () {
      /* The count is a convenience; a stale one is not worth a reload. */
    });
};

function settle() {
  window.wnRefreshCartBubble();

  var appDrawer = document.querySelector(".ocu-cart-drawer");
  if (!appDrawer) return window.location.reload();

  try {
    window.dispatchEvent(new CustomEvent("ocu:cart:update", { detail: { preventEvent: true } }));
    // Swapping out the line the app was drawing can leave the drawer shut behind the
    // customer. Asking it to open again is harmless when it already is.
    window.dispatchEvent(new CustomEvent("ocu:cart:open"));
  } catch (error) {
    window.location.reload();
  }
}

/*
 * The companion product at the bottom of the drawer: add it, do not swap.
 *
 * A separate listener rather than a branch in the one above, because the two do
 * opposite things to the cart. The pack button replaces a line; this one puts a
 * different product in beside what is there. Same `settle()` afterwards, for the
 * same reason: the app's drawer refreshes on its own event, and a reload would
 * close it on the customer.
 */
document.addEventListener("click", function (event) {
  var button = event.target.closest(".wn-crosssell__button");
  if (!button || button.disabled) return;

  var variantId = Number(button.dataset.variantId);
  if (!variantId) return;

  button.disabled = true;
  button.dataset.busy = "true";

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

  fetch(root + "cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // Same hidden property as the pack button, so the order shows the companion was
    // added from the cart.
    body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: { _added_from: "cart-companion" } }] }),
  })
    .then(function (response) {
      if (!response.ok) throw new Error("add failed");
      settle();
    })
    .catch(function () {
      window.location.reload();
    });
});
